import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Database,
  Users,
  AlertTriangle,
  BookOpen,
  Palette,
  Shield,
  UserCog,
  ScrollText,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react'
import { getAuditLog } from '../../services/api'
import type { AuditEntry } from '../../types'

interface StatCard {
  label: string
  value: string | number
  icon: React.ReactNode
  color: string
}

const QUICK_ACTIONS = [
  { label: 'Branding', path: '/admin/branding', icon: Palette, description: 'Customize portal appearance' },
  { label: 'Moderation', path: '/admin/moderation', icon: Shield, description: 'Review flagged content' },
  { label: 'User Management', path: '/admin/users', icon: UserCog, description: 'Manage user accounts' },
  { label: 'Audit Log', path: '/admin/audit', icon: ScrollText, description: 'View system activity' },
]

function formatTimestamp(ts: string): string {
  const d = new Date(ts)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return d.toLocaleDateString()
}

function actionIcon(action: string): React.ReactNode {
  switch (action) {
    case 'login':
      return <Users className="w-4 h-4 text-blue-500" />
    case 'search':
      return <Database className="w-4 h-4 text-purple-500" />
    case 'moderate':
      return <Shield className="w-4 h-4 text-amber-500" />
    case 'delete':
      return <XCircle className="w-4 h-4 text-red-500" />
    case 'add':
      return <CheckCircle className="w-4 h-4 text-green-500" />
    default:
      return <Activity className="w-4 h-4 text-gray-400" />
  }
}

export default function AdminDashboard() {
  const { data: auditEntries, isLoading } = useQuery<AuditEntry[]>({
    queryKey: ['admin', 'audit', 'recent'],
    queryFn: () => getAuditLog(),
    refetchInterval: 60000,
  })

  const recentEntries = (auditEntries ?? []).slice(0, 10)

  const stats: StatCard[] = [
    {
      label: 'Total Resources',
      value: '12,847',
      icon: <Database className="w-6 h-6 text-acad-500" />,
      color: 'border-acad-500',
    },
    {
      label: 'Active Users',
      value: '342',
      icon: <Users className="w-6 h-6 text-blue-500" />,
      color: 'border-blue-500',
    },
    {
      label: 'Moderation Queue',
      value: '7',
      icon: <AlertTriangle className="w-6 h-6 text-amber-500" />,
      color: 'border-amber-500',
    },
    {
      label: 'Subject Areas',
      value: '156',
      icon: <BookOpen className="w-6 h-6 text-green-500" />,
      color: 'border-green-500',
    },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-500 mt-1">Overview of your Academic Discovery Portal</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className={`bg-white rounded-lg shadow-sm p-6 border-l-4 ${stat.color}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {QUICK_ACTIONS.map((action) => (
            <Link
              key={action.path}
              to={action.path}
              className="bg-white rounded-lg shadow-sm p-5 border border-gray-100
                         hover:border-acad-300 hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-acad-50 rounded-lg group-hover:bg-acad-100 transition-colors">
                  <action.icon className="w-5 h-5 text-acad-600" />
                </div>
                <h3 className="font-medium text-gray-900">{action.label}</h3>
              </div>
              <p className="text-sm text-gray-500">{action.description}</p>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            {isLoading ? (
              <div className="p-8 text-center">
                <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2 animate-pulse" />
                <p className="text-sm text-gray-400">Loading activity...</p>
              </div>
            ) : recentEntries.length === 0 ? (
              <div className="p-8 text-center">
                <Activity className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No recent activity</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recentEntries.map((entry) => (
                  <li key={entry.event_id} className="px-5 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">{actionIcon(entry.action_type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {entry.user_id}
                          </p>
                          <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                            {formatTimestamp(entry.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 mr-2">
                            {entry.action_type}
                          </span>
                          {typeof entry.details === 'object'
                            ? JSON.stringify(entry.details).slice(0, 80)
                            : String(entry.details)}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* System Status */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">OpenSearch</span>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                Healthy
              </span>
            </div>
            <div className="border-t border-gray-100" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">API Version</span>
              <span className="text-sm font-medium text-gray-900"></span>
            </div>
            <div className="border-t border-gray-100" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Environment</span>
              <span className="text-sm font-medium text-gray-900">Development</span>
            </div>
            <div className="border-t border-gray-100" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Region</span>
              <span className="text-sm font-medium text-gray-900">ap-southeast-2</span>
            </div>
            <div className="border-t border-gray-100" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Uptime</span>
              <span className="text-sm font-medium text-gray-900">99.9%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
